import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './JsonFormatter.css'
import { parseJson, formatJson, jsonToYaml } from './utils/jsonTools'
import JsonTreeNode from './JsonTreeNode'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const EXAMPLE = '{\n  "name": "Jarder",\n  "tags": ["dev", "react"]\n}'

const JsonFormatter = () => {
  useDocumentMeta({
    title: 'Formatador e Validador de JSON Online | jarder.dev',
    description:
      'Formate, valide e visualize JSON em árvore, com aponta erros de ' +
      'sintaxe com linha e coluna. Exporte pra YAML. Grátis.',
  })

  const [input, setInput] = useState(EXAMPLE)
  const [result, setResult] = useState(null)
  const [view, setView] = useState('tree')

  const process = () => {
    const parsed = parseJson(input)
    setResult(parsed)
    trackEvent('tool_generate', { tool: 'json_formatter', valid: !parsed.error })
  }

  const copy = (text) => {
    navigator.clipboard.writeText(text)
    trackEvent('tool_copy', { tool: 'json_formatter' })
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Formatador e Validador de JSON</h1>
      <p className="tool-disclaimer">
        Cole um JSON, valide a sintaxe e visualize em árvore, tudo
        processado no seu navegador.
      </p>

      <div className="tool-field">
        <label htmlFor="json-input">JSON de entrada</label>
        <textarea
          id="json-input"
          rows="10"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
      </div>

      <button type="button" className="tool-button" onClick={process}>
        Validar e formatar
      </button>

      {result?.error && (
        <p className="tool-error">
          JSON inválido: {result.error.message}
          {result.error.line && (
            <> (linha {result.error.line}, coluna {result.error.column})</>
          )}
        </p>
      )}

      {result && !result.error && (
        <>
          <div className="json-view-tabs">
            <button
              type="button"
              className={`tool-button ${view === 'tree' ? '' : 'tool-button-secondary'}`}
              onClick={() => setView('tree')}
            >
              Árvore
            </button>
            <button
              type="button"
              className={`tool-button ${view === 'formatted' ? '' : 'tool-button-secondary'}`}
              onClick={() => setView('formatted')}
            >
              Formatado
            </button>
            <button
              type="button"
              className={`tool-button ${view === 'yaml' ? '' : 'tool-button-secondary'}`}
              onClick={() => setView('yaml')}
            >
              YAML
            </button>
          </div>

          {view === 'tree' && (
            <div className="json-tree">
              <JsonTreeNode value={result.value} />
            </div>
          )}
          {view === 'formatted' && (
            <div className="tool-field">
              <textarea rows="14" readOnly value={formatJson(result.value)} />
              <button
                type="button"
                className="tool-button tool-button-secondary"
                onClick={() => copy(formatJson(result.value))}
              >
                Copiar
              </button>
            </div>
          )}
          {view === 'yaml' && (
            <div className="tool-field">
              <textarea rows="14" readOnly value={jsonToYaml(result.value)} />
              <button
                type="button"
                className="tool-button tool-button-secondary"
                onClick={() => copy(jsonToYaml(result.value))}
              >
                Copiar
              </button>
            </div>
          )}
        </>
      )}

      <div className="tool-info-section">
        <h2>O que a validação verifica</h2>
        <p>
          JSON exige regras estritas de sintaxe: aspas duplas em chaves e
          strings, sem vírgula sobrando no último item, sem comentários.
          A ferramenta aponta exatamente a linha e coluna onde a sintaxe
          quebrou, o que ajuda a achar erros de digitação em arquivos de
          configuração ou respostas de API. A visualização em árvore
          facilita explorar objetos e arrays aninhados sem se perder na
          indentação, e a exportação para YAML é útil para arquivos de
          configuração (Docker Compose, GitHub Actions, Kubernetes).
        </p>
      </div>
    </div>
  )
}

export default JsonFormatter
